import { z } from 'zod';

/**
 * "Welcome Utazzer" - the internal CRM landing page every staff member sees
 * after sign-in (company story, vision, founders, live campaigns, values,
 * expectations).
 *
 * The page is an ordered list of typed sections - like a CMS custom page -
 * so an admin can add, reorder, hide or remove sections. The section types
 * are the page's own designs; any of them can be added more than once.
 *
 * Stored as ONE JSON blob in the existing `app_config` table under
 * `WELCOME_PAGE_CONFIG_KEY`, so it needs no schema change / migration. Until
 * an admin saves, `WELCOME_DEFAULT_SECTIONS` (the approved design) is served.
 *
 * Images are WebsiteMediaAsset ids (uploaded through the shared media
 * uploader); the read endpoint resolves them into `media` URLs.
 */

export const WELCOME_PAGE_CONFIG_KEY = 'welcome.page';

const MediaId = z.string().uuid();
const Text = (max: number) => z.string().max(max);
const ColourHex = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a hex colour like #093F51');

/** Icon keys the page knows how to draw (lucide icons + brand glyphs). */
export const WELCOME_ICON_KEYS = [
  'trending-up',
  'google',
  'facebook',
  'instagram',
  'youtube',
  'award',
  'users',
  'globe',
  'trophy',
  'star',
  'heart',
  'plane',
  'search',
  'sparkles',
  'shield-check',
  'lightbulb',
  'leaf',
  'target',
  'handshake',
  'rocket',
  'map-pin',
  'smile',
] as const;
export const WelcomeIconKeySchema = z.enum(WELCOME_ICON_KEYS);
export type WelcomeIconKey = z.infer<typeof WelcomeIconKeySchema>;

export const WELCOME_PLATFORMS = [
  'instagram',
  'facebook',
  'google',
  'youtube',
  'whatsapp',
] as const;
export const WelcomePlatformSchema = z.enum(WELCOME_PLATFORMS);
export type WelcomePlatform = z.infer<typeof WelcomePlatformSchema>;

// ---------------------------------------------------------------------------
// Section schemas. Field defaults are blank so a freshly added section starts
// empty; the design copy lives in WELCOME_DEFAULT_SECTIONS below.
// ---------------------------------------------------------------------------

const base = {
  /** Stable key for reordering / React lists. */
  id: z.string().min(1).max(40),
  visible: z.boolean().default(true),
};

export const WelcomeHeroSectionSchema = z.object({
  ...base,
  type: z.literal('hero'),
  /** Full designed banner (desktop). When set it replaces the built-in banner. */
  imageMediaId: MediaId.nullish(),
  /** Optional portrait crop for phones; falls back to `imageMediaId`. */
  mobileImageMediaId: MediaId.nullish(),
  titleTop: Text(40).default(''),
  titleBottom: Text(40).default(''),
  tagline: Text(160).default(''),
  taglineAccent: Text(60).default(''),
  pillars: z
    .array(z.object({ title: Text(40), body: Text(80).default('') }))
    .max(6)
    .default([]),
  footerLine: Text(80).default(''),
  footerAccent: Text(60).default(''),
});

export const WelcomeTextSectionSchema = z.object({
  ...base,
  type: z.literal('text'),
  heading: Text(120).default(''),
  /** Paragraphs split on blank lines. */
  body: Text(5000).default(''),
});

export const WelcomeTwoColumnSectionSchema = z.object({
  ...base,
  type: z.literal('twoColumn'),
  leftTitle: Text(60).default(''),
  left: Text(600).default(''),
  rightTitle: Text(60).default(''),
  right: Text(600).default(''),
});

export const WelcomePhotosSectionSchema = z.object({
  ...base,
  type: z.literal('photos'),
  heading: Text(120).default(''),
  body: Text(800).default(''),
  photoMediaIds: z.array(MediaId).max(30).default([]),
  ctaLabel: Text(60).default(''),
  ctaHref: Text(500).default(''),
});

export const WelcomePersonSchema = z.object({
  name: Text(80),
  role: Text(120).default(''),
  photoMediaId: MediaId.nullish(),
  quote: Text(1200).default(''),
});

export const WelcomePeopleSectionSchema = z.object({
  ...base,
  type: z.literal('people'),
  heading: Text(120).default(''),
  subheading: Text(160).default(''),
  items: z.array(WelcomePersonSchema).max(12).default([]),
});

export const WelcomeCampaignSchema = z.object({
  /** Destination name - also drives the filter circles above the grid. */
  destination: Text(60).default(''),
  imageMediaId: MediaId.nullish(),
  /** ISO date (YYYY-MM-DD) the creative went live. */
  startedOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullish(),
  platforms: z.array(WelcomePlatformSchema).max(5).default(['instagram']),
  /** Optional link to the live post / ad. */
  href: Text(500).default(''),
});

export const WelcomeCampaignsSectionSchema = z.object({
  ...base,
  type: z.literal('campaigns'),
  heading: Text(120).default(''),
  subheading: Text(160).default(''),
  destinations: z
    .array(z.object({ name: Text(60), imageMediaId: MediaId.nullish() }))
    .max(30)
    .default([]),
  items: z.array(WelcomeCampaignSchema).max(60).default([]),
  viewMoreLabel: Text(40).default('View More'),
  viewMoreHref: Text(500).default(''),
});

export const WelcomeStatSchema = z.object({
  icon: WelcomeIconKeySchema.default('star'),
  value: Text(40),
  label: Text(60).default(''),
});

export const WelcomeStatsSectionSchema = z.object({
  ...base,
  type: z.literal('stats'),
  heading: Text(120).default(''),
  items: z.array(WelcomeStatSchema).max(12).default([]),
});

export const WelcomeCardSchema = z.object({
  icon: WelcomeIconKeySchema.default('star'),
  /** Uploaded icon / logo; when set it replaces the built-in `icon`. */
  iconMediaId: MediaId.nullish(),
  title: Text(60),
  subtitle: Text(120).default(''),
  tagline: Text(200).default(''),
});

export const WelcomeCardsSectionSchema = z.object({
  ...base,
  type: z.literal('cards'),
  heading: Text(120).default(''),
  items: z.array(WelcomeCardSchema).max(12).default([]),
});

export const WelcomeStarSchema = z.object({
  name: Text(80),
  blurb: Text(280).default(''),
  photoMediaId: MediaId.nullish(),
  bgColour: ColourHex.default('#093F51'),
});

export const WelcomeStarsSectionSchema = z.object({
  ...base,
  type: z.literal('stars'),
  heading: Text(120).default(''),
  body: Text(400).default(''),
  /** `website` = reuse the "Stars Travelled With Us" cards managed in the Website CMS. */
  source: z.enum(['website', 'custom']).default('website'),
  items: z.array(WelcomeStarSchema).max(30).default([]),
});

export const WelcomeListItemSchema = z.object({
  title: Text(120),
  body: Text(800).default(''),
});

export const WelcomeListSectionSchema = z.object({
  ...base,
  type: z.literal('list'),
  heading: Text(120).default(''),
  /** `lines` = coloured left rule (Employee Expectations); `numbered` = 01, 02 cards. */
  style: z.enum(['lines', 'numbered']).default('lines'),
  items: z.array(WelcomeListItemSchema).max(20).default([]),
});

export const WelcomeBannerSectionSchema = z.object({
  ...base,
  type: z.literal('banner'),
  eyebrow: Text(120).default(''),
  title: Text(80).default(''),
  pillars: z.array(Text(40)).max(8).default([]),
  /** Optional illustration on the left (the suitcase in the design). */
  imageMediaId: MediaId.nullish(),
});

export const WelcomeSectionSchema = z.discriminatedUnion('type', [
  WelcomeHeroSectionSchema,
  WelcomeTextSectionSchema,
  WelcomeTwoColumnSectionSchema,
  WelcomePhotosSectionSchema,
  WelcomePeopleSectionSchema,
  WelcomeCampaignsSectionSchema,
  WelcomeStatsSectionSchema,
  WelcomeCardsSectionSchema,
  WelcomeStarsSectionSchema,
  WelcomeListSectionSchema,
  WelcomeBannerSectionSchema,
]);
export type WelcomeSection = z.infer<typeof WelcomeSectionSchema>;
export type WelcomeSectionType = WelcomeSection['type'];
export type WelcomeSectionOf<T extends WelcomeSectionType> = Extract<WelcomeSection, { type: T }>;
export type WelcomeCampaign = z.infer<typeof WelcomeCampaignSchema>;
export type WelcomeStar = z.infer<typeof WelcomeStarSchema>;

export const WELCOME_SECTION_TYPES = [
  'hero',
  'text',
  'twoColumn',
  'photos',
  'people',
  'campaigns',
  'stats',
  'cards',
  'stars',
  'list',
  'banner',
] as const satisfies ReadonlyArray<WelcomeSectionType>;

/** A blank section of `type` (what "Add section" inserts). */
export const newWelcomeSection = <T extends WelcomeSectionType>(
  type: T,
  id: string,
): WelcomeSectionOf<T> => WelcomeSectionSchema.parse({ type, id }) as WelcomeSectionOf<T>;

// ---------------------------------------------------------------------------
// Default page (the approved design copy). Served until an admin first saves.
// ---------------------------------------------------------------------------

const DEFAULT_SECTIONS_INPUT: ReadonlyArray<z.input<typeof WelcomeSectionSchema>> = [
  {
    id: 'hero',
    type: 'hero',
    titleTop: 'MOVING',
    titleBottom: 'THE NEEDLE',
    tagline: 'Small actions. Sharp focus. Bigger impact.',
    taglineAccent: 'Together.',
    pillars: [
      { title: 'Be Focused', body: 'Every action moves us forward' },
      { title: 'Move Together', body: 'One team. One goal. One direction' },
      { title: 'Raise the Bar', body: 'Challenge ourselves. Deliver excellence.' },
      { title: 'Create Impact', body: 'For our travellers. For each other' },
      { title: 'Own It', body: 'Take responsibility. Make it happen' },
    ],
    footerLine: 'One theme. One team.',
    footerAccent: 'Moving the needle',
  },
  {
    id: 'story',
    type: 'text',
    heading: 'Where It All Began',
    body: [
      'Travel planning should be stress-free for everyone. That simple truth is why Utazzo Holidays exists.',
      'Since 2012, we’ve been quietly fixing what the travel industry gets wrong. Back when we were called MakeYouTravel, our founders, Ashutosh Dwivedi and Prachi Dwivedi, noticed a pattern: travellers were spending more time worrying than actually travelling.',
      'So we flipped the script. We took on the chaos, the calculations, the fine print, and the “wait, is this included?” so travellers could just… travel.',
    ].join('\n\n'),
  },
  {
    id: 'vision-mission',
    type: 'twoColumn',
    leftTitle: 'Vision',
    left: 'To be India’s most trusted travel partner, known for creating stress-free, memorable journeys across the world.',
    rightTitle: 'Mission',
    right:
      'Crafting travel experiences as unique as every traveler, while delivering service that exceeds expectations at every step.',
  },
  {
    id: 'journey',
    type: 'photos',
    heading: '15+ Years. 100K+ Journeys. One Promise.',
    body: 'For 15 years, we’ve been turning travel dreams into unforgettable experiences, creating memories that last a lifetime while staying committed to exceptional service, trust, and care.',
    ctaLabel: 'What’s Cooking, Utazzers',
    ctaHref: 'https://www.instagram.com/utazzoholidays/',
  },
  {
    id: 'founders',
    type: 'people',
    heading: 'Founder’s Vision',
    subheading: 'The Vision That Moves Us',
    items: [
      {
        name: 'Ashutosh Dwivedi',
        role: 'Founder & CEO',
        quote:
          'At Utazzo Holidays, our vision has always been simple, a holiday should feel like a holiday, not a project to manage. We are building a brand that takes the complexity out of travel and puts the joy back into every journey. From our first steps with MakeYouTravel to building Utazzo Holidays as a trusted travel brand, our goal remains the same: to make every traveller’s experience truly stress-free.',
      },
      {
        name: 'Prachi (Sharma) Dwivedi',
        role: 'Co-Founder & Head of Operations',
        quote:
          'Great customer experiences begin with great teams. My focus at Utazzo Holidays is to build a culture where people take ownership, collaborate, and constantly raise the bar. With every process we improve and every person we empower, we move closer to our vision of delivering seamless, stress-free holidays: every single time.',
      },
    ],
  },
  {
    id: 'campaigns',
    type: 'campaigns',
    heading: 'What We’re Putting Out There',
    subheading: 'See the destinations we’re taking to the world',
    destinations: [
      { name: 'Vietnam' },
      { name: 'Thailand' },
      { name: 'Singapore' },
      { name: 'Kerala' },
    ],
    viewMoreHref: 'https://www.instagram.com/utazzoholidays/',
  },
  {
    id: 'stats',
    type: 'stats',
    heading: 'What Makes Us Different?',
    items: [
      { icon: 'trending-up', value: 'India’s Fastest', label: 'Growing Travel Brand' },
      { icon: 'google', value: '4.4 ★', label: '500+ reviews' },
      { icon: 'facebook', value: '53k', label: 'Followers' },
      { icon: 'instagram', value: '23k', label: 'Followers' },
      { icon: 'award', value: '15+', label: 'Years of Experience' },
      { icon: 'users', value: '100K+', label: 'Happy Travelers' },
      { icon: 'globe', value: '77K+', label: 'Social Community' },
      { icon: 'trophy', value: '25+', label: 'Awards' },
    ],
  },
  {
    id: 'values',
    type: 'cards',
    items: [
      {
        icon: 'plane',
        title: 'Stress-Free Holidays',
        subtitle: 'Panic isn’t on the itinerary.',
        tagline:
          'Kyuki bade bade shehron mein… chhoti chhoti baton ke liye jagah nahi hoti, Senorita.',
      },
      {
        icon: 'search',
        title: 'Transparency',
        subtitle: 'No fine print, only fine trips.',
        tagline: 'Parampara. Pratishtha. Anushasan.',
      },
      {
        icon: 'sparkles',
        title: 'Personalization',
        subtitle: 'fits like your fav hoodie',
        tagline: 'Tumhare paas destination hai, hamare paas customization.',
      },
      {
        icon: 'shield-check',
        title: 'Reliability',
        subtitle: 'on time, every time',
        tagline: 'Tension lene ka nahi, sirf dene ka.',
      },
      {
        icon: 'lightbulb',
        title: 'Innovation',
        subtitle: 'Cooler hacks, smarter travel',
        tagline: 'Picture abhi baaki hai mere dost.',
      },
      {
        icon: 'leaf',
        title: 'Sustainability',
        subtitle: 'See the world, don’t trash it',
        tagline: 'See the world, don’t trash it',
      },
    ],
  },
  {
    id: 'stars',
    type: 'stars',
    heading: 'Stars Travelled With Us',
    body: 'From Celebrated personalities to thousands of happy travellers, Utazzo is trusted for stress-free holidays.',
    source: 'website',
  },
  {
    id: 'employee-expectations',
    type: 'list',
    style: 'lines',
    heading: 'Employee Expectations',
    items: [
      {
        title: 'Timely Salary',
        body: 'Employees expect their salaries to be credited on time every month, ensuring financial stability and peace of mind.',
      },
      {
        title: 'Basic Workplace Amenities',
        body: 'Maintain a comfortable, safe, and well-equipped workplace with all the essential facilities required for employees to perform efficiently.',
      },
      {
        title: 'Attractive Incentive Structure',
        body: 'A transparent and rewarding incentive program that recognizes performance and allows employees to maximize their earnings based on their achievements.',
      },
      {
        title: 'Positive & Fun Work Environment',
        body: 'Foster a workplace where employees enjoy coming to work, collaborate with their teammates, and maintain a healthy work-life balance while staying productive.',
      },
      {
        title: 'Quality Leads & Growth Opportunities',
        body: 'Provide genuine and qualified leads that enable employees to achieve their targets, increase conversions, and earn higher incentives.',
      },
      {
        title: 'Recognition & Celebrations',
        body: 'Celebrate employee achievements, birthdays, festivals, work anniversaries, and milestones to create a culture of appreciation and belonging.',
      },
    ],
  },
  {
    id: 'employer-expectations',
    type: 'list',
    style: 'numbered',
    heading: 'Employer Expectations',
    items: [
      {
        title: 'Achieve Targets & Move the Needle',
        body: 'Take ownership of your goals and consistently meet targets within the defined timelines. Every contribution should create measurable business impact and help the organization move the business forward.',
      },
      {
        title: 'Sales Performance Expectations',
        body: 'From 100 leads every month, establish at least 40 meaningful customer connections. Achieve a minimum of 5 successful conversions every month. Continuously work towards improving your conversion rate through consistent follow-ups and customer engagement.',
      },
      {
        title: 'Deliver the Right Customer Experience',
        body: 'Understand the customer’s requirements during the very first interaction. Recommend the most suitable travel itinerary based on their needs and budget. Build trust by providing accurate information and realistic commitments. Never make false promises or commitments simply to close a sale.',
      },
      {
        title: 'Maintain a Clean & Updated CRM',
        body: 'Ensure that all customer interactions, follow-ups, remarks, and sales updates are recorded accurately in the CRM. A well-maintained CRM is essential for effective customer management and team collaboration.',
      },
      {
        title: 'Work Comes First',
        body: 'We encourage a positive and enjoyable work environment where everyone can have fun and celebrate success. However, delivering quality work, meeting commitments, and achieving targets must always remain the top priority.',
      },
      {
        title: 'Uphold Professionalism & Discipline',
        body: 'Maintain sincerity, punctuality, integrity, accountability, and professional conduct at all times. Respect company policies, colleagues, and customers while fostering a healthy workplace culture.',
      },
      {
        title: 'Lead by Example',
        body: 'Be someone who inspires others through your actions, ownership, consistency, and performance. Set high standards for yourself and contribute towards helping the team move the needle every day.',
      },
      {
        title: 'Our Success Mantra',
        body: 'Success at Utazzo is not measured only by the targets you achieve, but by the trust you build, the discipline you maintain, the value you create for every customer, and the positive impact you make every single day. Together, we don’t just meet expectations we move the needle.',
      },
    ],
  },
  {
    id: 'closing',
    type: 'banner',
    eyebrow: 'Every Enquiry, Every Call, Every Follow-Up.',
    title: 'KEEP MOVING THE NEEDLE',
    pillars: ['Be Focused', 'Move Together', 'Raise the Bar', 'Create Impact', 'Own It'],
  },
];

export const WELCOME_DEFAULT_SECTIONS: WelcomeSection[] = DEFAULT_SECTIONS_INPUT.map((s) =>
  WelcomeSectionSchema.parse(s),
);

export const WelcomePageContentSchema = z
  .object({
    sections: z
      .array(WelcomeSectionSchema)
      .max(40)
      .default(() => JSON.parse(JSON.stringify(WELCOME_DEFAULT_SECTIONS)) as WelcomeSection[]),
  })
  .superRefine((c, ctx) => {
    const seen = new Set<string>();
    c.sections.forEach((s, i) => {
      if (seen.has(s.id)) {
        ctx.addIssue({
          code: 'custom',
          path: ['sections', i, 'id'],
          message: 'Duplicate section id',
        });
      }
      seen.add(s.id);
    });
  });
export type WelcomePageContent = z.infer<typeof WelcomePageContentSchema>;

/** `GET /me/welcome-page` response. */
export const WelcomePageResponseSchema = z.object({
  content: WelcomePageContentSchema,
  /** The Website CMS "Stars Travelled With Us" cards, for stars sections with `source: 'website'`. */
  websiteStars: z.array(WelcomeStarSchema),
  /** media id → delivery URL for every image the page references. */
  media: z.record(z.string(), z.string()),
  updatedAt: z.string().nullable(),
  /** True until an admin saves the page for the first time. */
  isDefault: z.boolean(),
});
export type WelcomePageResponse = z.infer<typeof WelcomePageResponseSchema>;

/** `PUT /me/welcome-page` body (ADMIN only). */
export const UpdateWelcomePageRequestSchema = z.object({
  content: WelcomePageContentSchema,
});
export type UpdateWelcomePageRequest = z.infer<typeof UpdateWelcomePageRequestSchema>;

const sectionMediaIds = (s: WelcomeSection): Array<string | null | undefined> => {
  switch (s.type) {
    case 'hero':
      return [s.imageMediaId, s.mobileImageMediaId];
    case 'photos':
      return s.photoMediaIds;
    case 'people':
      return s.items.map((p) => p.photoMediaId);
    case 'campaigns':
      return [...s.destinations.map((d) => d.imageMediaId), ...s.items.map((i) => i.imageMediaId)];
    case 'cards':
      return s.items.map((x) => x.iconMediaId);
    case 'stars':
      return s.items.map((x) => x.photoMediaId);
    case 'banner':
      return [s.imageMediaId];
    default:
      return [];
  }
};

/** Every media id referenced by the content (for one batch URL lookup). */
export const collectWelcomeMediaIds = (c: WelcomePageContent): string[] => {
  const ids = c.sections.flatMap(sectionMediaIds);
  return [...new Set(ids.filter((x): x is string => typeof x === 'string' && x.length > 0))];
};
